import * as React from 'react';
import { usePortalUser } from 'vet-bones/utils/portal';

import { projectSelector } from 'src/selectors/project';
import {
  connectCollaborationForProject,
  disconnectCollaborationForProject,
} from 'src/utils/collaboration/collaborationLifecycle';
import { isCollaborationConfigured } from 'src/utils/collaboration/config';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

/**
 * Keep a project's collaboration WebSocket alive exactly while the project
 * route for `projectId` is mounted. Called from the project route wrapper.
 *
 * The effect is keyed on `[projectId, shouldConnect]` — a derived boolean — not
 * on the `project` object, so ordinary edits (which produce a new project
 * reference every change) do not churn the connection. This single effect
 * covers opening a project (connect), leaving the project path (cleanup
 * disconnects on unmount), and switching projects (cleanup disconnects the old
 * project, the re-run connects the new one).
 */
export const useCollaborationConnection = (projectId: string): void => {
  const portalUser = usePortalUser();
  const project = useReduxSelector(projectSelector(projectId));

  const shouldConnect = Boolean(
    project &&
      !project.isRemoved &&
      project.isCloudProject &&
      portalUser.data?.is_collaboration_enabled &&
      portalUser.data?.is_designer_storage_enabled &&
      isCollaborationConfigured()
  );

  React.useEffect(() => {
    if (!shouldConnect) {
      return undefined;
    }

    connectCollaborationForProject(projectId);
    return () => disconnectCollaborationForProject(projectId);
  }, [projectId, shouldConnect]);
};
